import React, { useState, useEffect } from 'react';

import AddIcon from '@mui/icons-material/Add';
import {
    Button,
    Container as MaterialContainer,
    Grid,
} from '@sunwisesoftware/sunwise-ui';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { compose } from 'redux';
import { createStructuredSelector } from 'reselect';

import { HeaderContainer, TitleIcon } from 'common/components';
import { PERMISSION_LIST } from 'common/constants/permissions';
import withPermissions from 'common/hocs/withPermissions';
import { getFinancierType, getFinancierId } from 'common/utils/session';

import financialFormsCore from '../financialFormsCore';
import * as financialFormsCoreActions from '../financialFormsCore/actions';
import * as financialFormsCoreSelectors from '../financialFormsCore/selectors';

import * as actions from './actions';
import ListItems from './components/ListItems';
import * as selectors from './selectors';

const Container = ({
    canDelete,
    canModify,
    fetchProducts,
    handleClickBulkItems,
    handleClickDuplicate,
    handleSaveRow,
    isFetching,
    isFetchingBulk,
    paginationData,
    prepareCreate,
    prepareUpdate,
    productModalIsOpen,
    products,
}) => {
    const { t } = useTranslation();
    const [filterData, setFilterData] = useState({
        orderBy: 'created_at',
        page: 0,
        pageSize: 25,
        sortBy: 'desc',
        status: 'active',
    });

    const financierId = getFinancierId();
    const financierType = getFinancierType();

    const handleClickCreate = () => prepareCreate(financierId);
    const handleClickPreview = (product) =>
        prepareUpdate({ ...product, disabled: true, financier: financierId });
    const handleClickUpdate = (product) =>
        prepareUpdate({ ...product, disabled: false, financier: financierId });

    const commonProps = {
        callback: () => fetchProducts(filterData),
        canDelete,
        filterData,
        financierId,
        financierType,
        handleClickBulkItems,
    };

    useEffect(() => {
        fetchProducts(filterData);
    }, []);

    return (
        <MaterialContainer maxWidth={false}>
            <HeaderContainer>
                <Grid container alignItems="center" spacing={1}>
                    <Grid size="grow">
                        <TitleIcon title={t('Alliance Products')} />
                    </Grid>

                    {canModify && (
                        <Grid>
                            <Button
                                endIcon={<AddIcon />}
                                onClick={handleClickCreate}
                                sx={{ width: { md: 'auto', xs: '100%' } }}
                                variant="outlined"
                            >
                                {t('Add Product')}
                            </Button>
                        </Grid>
                    )}
                </Grid>
            </HeaderContainer>

            <ListItems
                canDelete={canDelete}
                canModify={canModify}
                filterData={filterData}
                filterItems={fetchProducts}
                handleClickBulkItems={handleClickBulkItems}
                handleClickDuplicate={handleClickDuplicate}
                handleClickPreview={handleClickPreview}
                handleClickUpdate={handleClickUpdate}
                handleSaveRow={handleSaveRow}
                isFetching={isFetching || isFetchingBulk}
                items={products}
                paginationData={paginationData}
                setFilterData={setFilterData}
            />

            {productModalIsOpen && (
                <financialFormsCore.Container {...commonProps} />
            )}
        </MaterialContainer>
    );
};

const mapStateToProps = createStructuredSelector({
    isFetching: selectors.getProductsIsFetching,
    isFetchingBulk: selectors.getIsFetchingBulk,
    paginationData: selectors.getProductsPagination,
    productModalIsOpen: financialFormsCoreSelectors.getProductModalIsOpen,
    products: selectors.getProductsData,
});

const mapDispatchToProps = (dispatch) => ({
    fetchProducts: (filterData) => dispatch(actions.fetchProducts(filterData)),
    handleClickBulkItems: (ids, value, filterData, callback) =>
        dispatch(actions.prepareBulkItems(ids, value, filterData, callback)),
    handleClickDuplicate: (product) =>
        dispatch(actions.duplicateProduct(product)),
    handleSaveRow: (filterData, item) =>
        dispatch(actions.updateProduct(filterData, item)),
    prepareCreate: (financierId) =>
        dispatch(financialFormsCoreActions.prepareCreate(financierId)),
    prepareUpdate: (value) =>
        dispatch(financialFormsCoreActions.prepareUpdate(value)),
});

Container.propTypes = {
    canDelete: PropTypes.bool,
    canModify: PropTypes.bool,
    fetchProducts: PropTypes.func,
    handleClickBulkItems: PropTypes.func,
    handleClickDuplicate: PropTypes.func,
    handleSaveRow: PropTypes.func,
    isFetching: PropTypes.bool,
    isFetchingBulk: PropTypes.bool,
    paginationData: PropTypes.object,
    prepareCreate: PropTypes.func,
    prepareUpdate: PropTypes.func,
    productModalIsOpen: PropTypes.bool,
    products: PropTypes.array,
};

export default compose(
    connect(mapStateToProps, mapDispatchToProps),
    withPermissions(PERMISSION_LIST.ALLIANCE_PRODUCTS_PERMISSION),
)(Container);
